import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {createSelector, Store} from '@ngrx/store';
import {AppState} from '../store';
import {TenantInfo} from '../models/tenant.model';
import {UserPermissions} from '../models/user-permissions.model';
import {selectTenantInfo, selectUserPermissions} from '../store/selectors';
import {environment} from '../../environments/environment';
import {isPlanAllowed} from '../shared/allow-plan/is-plan-allowed';
import {Sha256} from '@aws-crypto/sha256-browser'
import {toHexString} from '../common/crypto-utils';
import {CookieService} from 'ngx-cookie-service';
import {TenantService} from '../services/tenant.service';
import {checkIfPlatformSite, getPlatformSubdomain} from '../common/platform-utils';
import { runAfterUserInteraction, runWhenIdle } from '../common/idle-utils';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

declare const fbq;

interface FacebookIntegrationData {
  tenant: TenantInfo,
  permissions: UserPermissions

}

const selectFacebookIntegrationData = createSelector(
  selectTenantInfo,
  selectUserPermissions,
  (tenant, permissions) => {
    return {
      tenant,
      permissions
    }
  }
);

// these subdomains are excluded from the Facebook platform pixel installation
const excludedSubdomains = [
  'living-lights',
  'brandgaytor',
  'herewithin',
  'shamanicsoulstaracademy',
  'bamboobeginnings',
  'christinegold',
  'healingwiththerev'
];
@Component({
    selector: "facebook-integration",
    template: "",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FacebookIntegrationComponent  {
  private store = inject<Store<AppState>>(Store);
  private cookies = inject(CookieService);
  private tenantService = inject(TenantService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private readonly tenantInfo = toSignal(this.store.select(selectTenantInfo));
  private readonly permissions = toSignal(this.store.select(selectUserPermissions));
  private readonly pixelInitialized = signal(false);

  private navigationTrackingStarted = false;

  constructor() {
    effect(async () => {
      const permissions = this.permissions();
      const tenant = this.tenantInfo();
      
      if (permissions && !this.pixelInitialized()) {
        await this.initFacebookPixel({permissions, tenant});
      }
    });

  }

  private async initFacebookPixel(data: FacebookIntegrationData) {

    console.log(`Setting up Facebook Pixel`, data);

    const {permissions} = data;

    const tenant = this.tenantService?.tenant;

    const facebookConfig = tenant?.facebookConfig;

    // determine which Facebook pixel to use - platform or course creator pixel
    let facebookPixelId = null;

    const accountCreationEvent = location.search?.includes('accountCreationEvent=true');

    const platformPlanPurchasedEvent = location.search?.includes("platformPlanPurchasedEvent=true");

    const isStudent = !permissions?.isAdmin && !permissions?.isCourseAdmin && !permissions?.isPlatformAdmin;

    console.log(`FACEBOOK accountCreationEvent: ${accountCreationEvent} | ${location.search}`);

    const currentSubdomain = getPlatformSubdomain();

    // Are we on a domain that belongs to OnlineCourseHost, rather than on the school's own
    // branded domain? getPlatformSubdomain() only matches *.onlinecoursehost.com.
    const onPlatformOwnedDomain = !!currentSubdomain || checkIfPlatformSite();

    /**
     * The platform pixel is OnlineCourseHost's own remarketing pixel. It belongs on our own
     * domains, and on the two platform conversion events (a school signing up, a school buying a
     * plan) wherever those happen.
     *
     * It must NOT load on a school's own custom domain just because the school owner happens to
     * be logged in. That is what made this look broken from the outside: the owner opens their
     * own site, sees pixel 501280424286480 in window.fbq instead of their own, and no events at
     * all - because the PageView below is deliberately suppressed for admins. Meta's Event Setup
     * Tool opens the site in that same logged-in browser, so it reported the school's pixel as
     * "not detected" too. Their real visitors were always tracked correctly.
     */
    const usePlatformPixel = accountCreationEvent || platformPlanPurchasedEvent ||
      (permissions.isAdmin && onPlatformOwnedDomain);

    if (usePlatformPixel) {

      if (excludedSubdomains.includes(currentSubdomain)) {
        console.log(`FACEBOOK Facebook Pixel NOT installed: subdomain ${currentSubdomain} is excluded.`);
        return;
      }

      facebookPixelId = environment.facebook.platformPixelId;
      console.log(`FACEBOOK Platform Facebook Pixel Installed, Pixel Id ${facebookPixelId}`);
    }
    // the creator pixel: the school has at least the Pro plan, the integration is enabled, and we
    // are on their own custom domain. Admins get it installed too, so that Meta's Event Setup Tool
    // and Pixel Helper can verify it - but no PageView is reported for them, see below, so their
    // own browsing never pollutes their statistics.
    else if (tenant && isPlanAllowed(tenant, "pro") && facebookConfig?.enabled && !currentSubdomain) {
      facebookPixelId = facebookConfig?.facebookCreatorPixel;
      console.log(`FACEBOOK Course Creator Facebook Pixel Installed, Pixel Id ${facebookPixelId}`);
    } else {
      console.log("FACEBOOK Facebook pixel not installed.");
    }

    if (facebookPixelId) {
      runAfterUserInteraction(async () => {
        console.log(`FACEBOOK Installing the Facebook Pixel with Pixel Id ${facebookPixelId}`);

        // install the Facebook pixel script - with either the platform pixel or the course creator pixel
        this.ensureFacebookPixelScript();

        if (fbq) {

          console.log(`FACEBOOK Initializing the Facebook tracking code.`)

          // The email hash identifies the SCHOOL OWNER to our own Meta account, so it only makes
          // sense alongside the platform pixel. It must not be attached to a school's own pixel.
          if (usePlatformPixel) {

            console.log(`FACEBOOK Initializing the Facebook tracking code WITH an email hash.`)

            // hash the customer email
            const hash = new Sha256();
            hash.update(data?.tenant['email']);
            const output = await hash.digest();
            const hashedEmail = toHexString(output);

            console.log(`FACEBOOK Hashed email ${data?.tenant['email']} ${hashedEmail}`)

            // get the Facebook click Id
            const fbcid = this.cookies.get('_fbc');

            console.log(`FACEBOOK Content of the _fbc cookie: ${fbcid}`);

            const  fbc = fbcid?.split('.')?.pop();

            console.log(`FACEBOOK Extracted Facebook click Id (fbc): ${fbc}`);

            const dataSentToFacebook:any = {
              em: hashedEmail
            };

            if (fbc) {
              dataSentToFacebook.fbc = fbc;
            }

            console.log(`FACEBOOK Data sent to Facebook: ${JSON.stringify(dataSentToFacebook)}`);

            // initialize the tracking code
            // docs https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
            fbq('init', facebookPixelId, dataSentToFacebook);
          }
          else {
          console.log(`Initializing the Facebook tracking code without email hash.`)
          fbq('init', facebookPixelId);
          }

        // report a page view event - don't report page views for admins
          if (isStudent && facebookConfig?.enabled) {
            fbq('track', 'PageView');
            // Meta only ever saw this one PageView per visit. This is a single page app, so every
            // later route change has to be reported by hand, exactly as the Google tag does.
            this.reportPageViewsOnNavigation();
          }
        }
      });

      this.pixelInitialized.set(true);
    }

  }

  /**
   * fbq('track','PageView') on each in-app route change.
   *
   * Without this Meta sees a single PageView for the whole visit: a student who lands on /courses
   * and then opens a course sales page produces no second PageView, so the sales page never
   * becomes a retargetable audience and campaign optimisation has almost nothing to learn from.
   */
  private reportPageViewsOnNavigation() {

    if (this.navigationTrackingStarted) {
      return;
    }
    this.navigationTrackingStarted = true;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        runWhenIdle(() => {
          if (typeof fbq === 'function') {
            console.log(`FACEBOOK Reporting PageView for ${location.pathname}`);
            fbq('track', 'PageView');
          }
        });
      });
  }

  private ensureFacebookPixelScript() {
    const win = window as any;
    if (!win.fbq) {
      const fbq = function (...args: any[]) {
        if ((fbq as any).callMethod) {
          (fbq as any).callMethod.apply(fbq, args);
        } else {
          (fbq as any).queue.push(args);
        }
      } as any;

      (fbq as any).queue = [];
      (fbq as any).loaded = true;
      (fbq as any).version = '2.0';
      win.fbq = fbq;
      win._fbq = fbq;
    }

    if (!document.querySelector('script[data-facebook-pixel]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      script.setAttribute('data-facebook-pixel', 'true');

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    }
  }


}



